import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import axios, { setCurrentUserTimezone } from '@/lib/axios'
import { UserType } from '@/types/user'
import { getErrorMessage } from '@/lib/utils'

type MeStateType = {
  isLoading: boolean
  response: UserType | null
  error: {
    status?: number
    message: string
  } | null
}

type MeStoreType = {
  meState: MeStateType
  getMe: () => Promise<UserType>
  editProfile: (profileData: any) => Promise<UserType>
  reset: () => void
}

const initialState: MeStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useMeStore = create<MeStoreType>()(
  devtools((set, get) => ({
    meState: initialState,
    getMe: async () => {
      // If already loading, don't make another request
      const currentState = get().meState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        meState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.get('/account/me')

        // Set timezone for future API calls
        if (data.timezone) {
          setCurrentUserTimezone(data.timezone)
        }

        set(() => ({
          meState: {
            ...initialState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          meState: {
            ...get().meState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },
    editProfile: async (profileData: any) => {
      try {
        const { data } = await axios.patch('/account/update', profileData, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        set(() => ({
          meState: {
            ...get().meState,
            response: data,
            error: null
          }
        }))

        // Update timezone if it changed
        if (data.timezone) {
          setCurrentUserTimezone(data.timezone)
        }

        return data
      } catch (error: any) {
        throw error
      }
    },
    reset: () => {
      setCurrentUserTimezone(null)
      set(() => ({
        meState: initialState
      }))
    }
  }))
)

export default useMeStore
